import * as React from 'react';
import { useLocation, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink as ShadcnBreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
} from '@/components/ui/breadcrumb';
import { BreadcrumbLink } from '@/types/navigation';
import { truncateBreadcrumbs } from '@/utils/navigation-utils';
import { cn } from '@/lib/utils';
import { useBreadcrumbSegmentOverrides } from '@/contexts/BreadcrumbOverrideContext';
import { useAuth } from '@/stores/use-auth';
import { INSPECT_QUALITY_PATH, isInspectClassicChildPath } from '@/lib/inspect-navigation';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Path prefixes that appear in URLs but have no real parent page. When a
 * breadcrumb segment matches one of these, it is rendered as plain text
 * (non-clickable) instead of a link, so clicking it cannot 404.
 *
 * `/dashboards` groups `/dashboards/{teams,repositories,requirements}` but has
 * no index page; `/dashboards/requirements/authors` is only ever followed by an
 * author email (DC-494).
 */
const SEGMENTS_WITHOUT_PARENT_ROUTE = new Set<string>([
  '/sorcery/feature-flags',
  '/dashboards',
  '/dashboards/requirements/authors',
]);

function createUuidDetailPathParser(
  firstSegment: string,
  secondSegment?: string
): (pathname: string) => { id: string } | null {
  return (pathname: string) => {
    const parts = pathname.split('/').filter(Boolean);
    const expectedLength = secondSegment ? 3 : 2;
    if (parts.length !== expectedLength) return null;
    if (parts[0] !== firstSegment) return null;
    if (secondSegment && parts[1] !== secondSegment) return null;
    const candidate = parts[expectedLength - 1];
    return candidate && UUID_REGEX.test(candidate) ? { id: candidate } : null;
  };
}

function parseTeamDashboardPath(pathname: string): { teamId: string } | null {
  const parts = pathname.split('/').filter(Boolean);
  const teamsIdx = parts.indexOf('teams');
  if (teamsIdx === -1 || teamsIdx + 2 >= parts.length) return null;
  const teamId = parts[teamsIdx + 1];
  const nextSegment = parts[teamsIdx + 2];
  if (teamId && UUID_REGEX.test(teamId) && nextSegment === 'dashboard') {
    return { teamId };
  }
  return null;
}

function parseEngineerReportPath(pathname: string): { engineerId: string } | null {
  const parts = pathname.split('/').filter(Boolean);
  const engineersIdx = parts.indexOf('engineers');
  if (engineersIdx === -1 || engineersIdx + 1 >= parts.length) return null;
  const engineerId = parts[engineersIdx + 1];
  if (engineerId && UUID_REGEX.test(engineerId)) {
    return { engineerId };
  }
  return null;
}

const parseGovernAdrDetailPath = createUuidDetailPathParser('govern', 'adrs');
const parseGovernCodingStandardDetailPath = createUuidDetailPathParser(
  'govern',
  'coding-standards'
);

function parsePrReviewDetailPath(pathname: string): { prId: string } | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length !== 2 || parts[0] !== 'reviews') return null;
  const prId = parts[1];
  return prId ? { prId } : null;
}

/**
 * `/dashboards/requirements/authors/:email` — the last segment is a URL-encoded
 * email (`%40`), which `location.pathname` does not decode. Returns the decoded
 * email so the label reads naturally and so the page can register a display-name
 * override keyed by the same value `useParams` gives it.
 */
function parseRequirementsAuthorPath(pathname: string): { email: string } | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length !== 4) return null;
  if (parts[0] !== 'dashboards' || parts[1] !== 'requirements' || parts[2] !== 'authors') {
    return null;
  }
  const raw = parts[3];
  if (!raw) return null;
  try {
    return { email: decodeURIComponent(raw) };
  } catch {
    return { email: raw };
  }
}

function parseWorkItemDetailPath(pathname: string): { workItemId: string } | null {
  const parts = pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('work-items');
  if (idx === -1 || idx + 1 >= parts.length) return null;
  const workItemId = parts[idx + 1];
  return workItemId ? { workItemId } : null;
}

/** Team audit logs live under /organization/:id/audit-logs but UX trail is Settings → Team. */
function isOrganizationAuditLogsPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  return (
    parts.length === 3 &&
    parts[0] === 'organization' &&
    Boolean(parts[1] && UUID_REGEX.test(parts[1])) &&
    parts[2] === 'audit-logs'
  );
}

function defaultSegmentLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
}

interface BreadcrumbsProps {
  className?: string;
}

type DynamicSegmentResolver = {
  label: string;
  isCurrent: boolean;
};

export function Breadcrumbs({ className }: BreadcrumbsProps) {
  const { t } = useTranslation('layout');
  const location = useLocation();
  const params = useParams();
  const segmentOverrides = useBreadcrumbSegmentOverrides();
  const eligibleForNextShell = useAuth(
    (s) => s.user?.nextNavigation?.eligibleForNextShell ?? false
  );
  const pathnames = location.pathname.split('/').filter((x) => x);
  const showInspectParentCrumb =
    eligibleForNextShell && isInspectClassicChildPath(location.pathname);

  const teamDashboard = parseTeamDashboardPath(location.pathname);
  const teamId = teamDashboard?.teamId ?? params['teamId'];
  const isTeamDashboard = !!teamId && teamDashboard !== null;
  const teamIdOverride = isTeamDashboard && teamId ? segmentOverrides[teamId] : null;

  const engineerReport = parseEngineerReportPath(location.pathname);
  const engineerId = engineerReport?.engineerId ?? params['id'];
  const isEngineerReport = !!engineerId && engineerReport !== null;
  const engineerIdOverride = isEngineerReport && engineerId ? segmentOverrides[engineerId] : null;

  const prReviewDetail = parsePrReviewDetailPath(location.pathname);
  const prIdForBreadcrumb = prReviewDetail?.prId ?? params['prId'];
  const isPrReviewDetail = !!prIdForBreadcrumb && prReviewDetail !== null;
  const prIdOverride =
    isPrReviewDetail && prIdForBreadcrumb ? segmentOverrides[prIdForBreadcrumb] : null;

  const workItemDetail = parseWorkItemDetailPath(location.pathname);
  const workItemIdForBreadcrumb =
    workItemDetail?.workItemId ?? (pathnames.includes('work-items') ? params['id'] : undefined);
  const isWorkItemDetail = !!workItemIdForBreadcrumb && workItemDetail !== null;
  const workItemIdOverride =
    isWorkItemDetail && workItemIdForBreadcrumb ? segmentOverrides[workItemIdForBreadcrumb] : null;

  const governAdrDetail = parseGovernAdrDetailPath(location.pathname);
  const adrIdForBreadcrumb = governAdrDetail?.id ?? params['adrId'];
  const isGovernAdrDetail = !!adrIdForBreadcrumb && governAdrDetail !== null;
  const adrIdOverride =
    isGovernAdrDetail && adrIdForBreadcrumb ? segmentOverrides[adrIdForBreadcrumb] : null;

  const governCodingStandardDetail = parseGovernCodingStandardDetailPath(location.pathname);
  const standardIdForBreadcrumb = governCodingStandardDetail?.id ?? params['standardId'];
  const isGovernCodingStandardDetail =
    !!standardIdForBreadcrumb && governCodingStandardDetail !== null;
  const standardIdOverride =
    isGovernCodingStandardDetail && standardIdForBreadcrumb
      ? segmentOverrides[standardIdForBreadcrumb]
      : null;

  const requirementsAuthor = parseRequirementsAuthorPath(location.pathname);
  const requirementsAuthorOverride = requirementsAuthor
    ? segmentOverrides[requirementsAuthor.email]
    : null;

  const teamIdSegmentIndex = teamId ? pathnames.indexOf(teamId) : -1;
  const engineerIdSegmentIndex = engineerId ? pathnames.indexOf(engineerId) : -1;
  const prIdSegmentIndex = prIdForBreadcrumb ? pathnames.indexOf(prIdForBreadcrumb) : -1;
  const workItemIdSegmentIndex = workItemIdForBreadcrumb
    ? pathnames.indexOf(workItemIdForBreadcrumb)
    : -1;
  const adrIdSegmentIndex = adrIdForBreadcrumb ? pathnames.indexOf(adrIdForBreadcrumb) : -1;
  const standardIdSegmentIndex = standardIdForBreadcrumb
    ? pathnames.indexOf(standardIdForBreadcrumb)
    : -1;
  const skipDashboardSegment = isTeamDashboard && pathnames[pathnames.length - 1] === 'dashboard';

  const resolveSegmentLabel = (value: string): string => {
    const segmentKey = `breadcrumbs.segments.${value}`;
    const translated = t(segmentKey, { defaultValue: '' });
    return translated || defaultSegmentLabel(value);
  };

  const auditLogsBreadcrumbOverride = isOrganizationAuditLogsPath(location.pathname);
  const dynamicSegmentByIndex: Record<number, DynamicSegmentResolver | undefined> = {
    ...(isTeamDashboard && teamIdSegmentIndex >= 0
      ? {
          [teamIdSegmentIndex]: {
            label: teamIdOverride ?? t('breadcrumbs.team'),
            isCurrent: skipDashboardSegment || teamIdSegmentIndex === pathnames.length - 1,
          },
        }
      : {}),
    ...(isEngineerReport && engineerIdSegmentIndex >= 0
      ? {
          [engineerIdSegmentIndex]: {
            label: engineerIdOverride ?? engineerId ?? '',
            isCurrent: engineerIdSegmentIndex === pathnames.length - 1,
          },
        }
      : {}),
    ...(isPrReviewDetail && prIdSegmentIndex >= 0 && prIdForBreadcrumb
      ? {
          [prIdSegmentIndex]: {
            label: prIdOverride ?? defaultSegmentLabel(prIdForBreadcrumb),
            isCurrent: prIdSegmentIndex === pathnames.length - 1,
          },
        }
      : {}),
    ...(isWorkItemDetail && workItemIdSegmentIndex >= 0 && workItemIdForBreadcrumb
      ? {
          [workItemIdSegmentIndex]: {
            label: workItemIdOverride ?? defaultSegmentLabel(workItemIdForBreadcrumb),
            isCurrent: workItemIdSegmentIndex === pathnames.length - 1,
          },
        }
      : {}),
    ...(isGovernAdrDetail && adrIdSegmentIndex >= 0 && adrIdForBreadcrumb
      ? {
          [adrIdSegmentIndex]: {
            label: adrIdOverride ?? defaultSegmentLabel(adrIdForBreadcrumb),
            isCurrent: adrIdSegmentIndex === pathnames.length - 1,
          },
        }
      : {}),
    ...(isGovernCodingStandardDetail && standardIdSegmentIndex >= 0 && standardIdForBreadcrumb
      ? {
          [standardIdSegmentIndex]: {
            label: standardIdOverride ?? defaultSegmentLabel(standardIdForBreadcrumb),
            isCurrent: standardIdSegmentIndex === pathnames.length - 1,
          },
        }
      : {}),
    // Author email is always the 4th segment and always the current page; the
    // detail page swaps in the display name once loaded (DC-494).
    ...(requirementsAuthor
      ? {
          3: {
            label: requirementsAuthorOverride ?? requirementsAuthor.email,
            isCurrent: true,
          },
        }
      : {}),
  };

  const segments = auditLogsBreadcrumbOverride
    ? []
    : pathnames
        .map((value, index) => {
          const path = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isCurrent = index === pathnames.length - 1;
          const dynamicSegment = dynamicSegmentByIndex[index];
          if (dynamicSegment) {
            return {
              label: dynamicSegment.label,
              path,
              isCurrent: dynamicSegment.isCurrent,
            };
          }
          if (skipDashboardSegment && index === pathnames.length - 1 && value === 'dashboard') {
            return null;
          }

          if (value === 'reviews') {
            return {
              label: t('breadcrumbs.pullRequests'),
              path: '/pull-requests',
              isCurrent,
            };
          }
          const label = resolveSegmentLabel(value);
          return { label, path, isCurrent };
        })
        .filter((s): s is NonNullable<typeof s> => s !== null);

  const segmentsWithInspectParent = showInspectParentCrumb
    ? [
        {
          label: t('nav.inspect'),
          path: INSPECT_QUALITY_PATH,
          isCurrent: false,
        },
        ...segments,
      ]
    : segments;

  const breadcrumbs: BreadcrumbLink[] = auditLogsBreadcrumbOverride
    ? [
        {
          label: t('breadcrumbs.dashboard'),
          path: '/dashboard',
          isCurrent: false,
        },
        {
          label: resolveSegmentLabel('settings'),
          path: '/settings',
          isCurrent: false,
        },
        {
          label: t('breadcrumbs.team'),
          path: '/settings/team',
          isCurrent: false,
        },
        {
          label: resolveSegmentLabel('audit-logs'),
          path: location.pathname,
          isCurrent: true,
        },
      ]
    : [
        {
          label: t('breadcrumbs.dashboard'),
          path: '/dashboard',
          isCurrent: location.pathname === '/dashboard',
        },
        ...segmentsWithInspectParent.filter((s) => s.path !== '/dashboard'),
      ];

  const uniqueBreadcrumbs = breadcrumbs.filter((b, index) => {
    if (index === 0) return true;
    return b.path !== '/dashboard';
  });

  const items = truncateBreadcrumbs(uniqueBreadcrumbs);

  return (
    <Breadcrumb aria-label={t('breadcrumbs.ariaLabel')} className={cn(className)}>
      <BreadcrumbList>
        {items.map((item, index) => {
          if ('isEllipsis' in item) {
            return (
              <React.Fragment key="ellipsis">
                <BreadcrumbItem>
                  <BreadcrumbEllipsis />
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </React.Fragment>
            );
          }

          const isLast = index === items.length - 1;
          const isNonClickable = SEGMENTS_WITHOUT_PARENT_ROUTE.has(item.path);

          return (
            <React.Fragment key={item.path}>
              <BreadcrumbItem>
                {isLast || isNonClickable ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : (
                  <ShadcnBreadcrumbLink asChild>
                    <Link to={item.path}>{item.label}</Link>
                  </ShadcnBreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
